import React from "react";
import { useNavigate } from "react-router-dom";
import ListingsPreviewGrid from "../SearchResults/ListingsPreviewGrid";
import PeoplePreviewRow from "../SearchResults/PeoplePreviewRow";
import { PHASE_LABELS } from "./useCvantoChat";

export const AiMark = () => (
  <span className="cvai-mark" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3c.9 4.6 3.6 7.3 8.2 8.2-4.6.9-7.3 3.6-8.2 8.2-.9-4.6-3.6-7.3-8.2-8.2C8.4 10.3 11.1 7.6 12 3z"
        fill="currentColor"
      />
    </svg>
  </span>
);

const TypingDots = () => (
  <span className="cvai-typing" aria-hidden="true">
    <i />
    <i />
    <i />
  </span>
);

// One chat turn. Marketplace results are STRUCTURED UI (the same listing tiles
// and people row the search page uses), never a text list written by the
// model; the text above them is a short, template-built sentence.
// `hideResults` — Unified Search Agent panel (handoff §25.5): "conversation
// controls search; SearchResults displays marketplace inventory" — cards
// belong in the main catalogue, not duplicated inside the chat bubble. The
// standalone /ai page (CvantoAIPage.js) keeps the default (inline grid).
export default function MessageBubble({ message, isLast, sending, onAction, onRetry, hideResults = false }) {
  const navigate = useNavigate();
  const isUser = message.role === "user";
  const results = message.results;
  const listings = results?.listings || [];
  const people = results?.people || [];

  const openListing = (item) => navigate(`/listing/${item.listingId || item.id}`);
  const openProfile = (id) => navigate(`/profile/${id}`);
  const showAll = () => navigate(`/search?q=${encodeURIComponent(results?.query || "")}`);

  if (isUser) {
    return (
      <div className={`cvai-msg cvai-msg--user ${message.pending ? "is-pending" : ""}`}>
        <div className="cvai-bubble cvai-bubble--user">{message.text}</div>
      </div>
    );
  }

  const waiting = message.pending && !message.text;

  return (
    <div className="cvai-msg cvai-msg--ai">
      <AiMark />

      <div className="cvai-ai-body">
        {waiting && (
          <div className="cvai-bubble cvai-bubble--ai cvai-bubble--status" role="status">
            <TypingDots />
            <span>{PHASE_LABELS[message.phase] || PHASE_LABELS.understanding}</span>
          </div>
        )}

        {!!message.text && (
          <div className={`cvai-bubble cvai-bubble--ai ${message.limit ? "cvai-bubble--notice" : ""}`}>{message.text}</div>
        )}

        {message.pending && !!message.text && (
          <div className="cvai-inline-status" role="status">
            <TypingDots />
          </div>
        )}

        {!hideResults && (listings.length > 0 || people.length > 0) && (
          <div className="cvai-results">
            {listings.length > 0 && (
              <ListingsPreviewGrid items={listings} onOpen={openListing} hideHeading />
            )}
            {people.length > 0 && (
              <PeoplePreviewRow users={people} onOpenProfile={openProfile} hideHeading />
            )}
            {listings.length > 0 && !!results?.query && (
              <button type="button" className="cvai-link-btn" onClick={showAll}>
                Показати всі результати
              </button>
            )}
          </div>
        )}

        {!!message.actions?.length && !message.pending && (
          <div className="cvai-actions">
            {message.actions.map((a) => (
              <button
                key={a.type}
                type="button"
                className="cvai-action-btn"
                disabled={sending || !isLast}
                onClick={() => onAction(a.type)}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}

        {!!message.requestId && (
          <button type="button" className="cvai-link-btn" onClick={() => navigate(`/request/${message.requestId}`)}>
            Переглянути запит
          </button>
        )}

        {message.limit?.upgradePlan && (
          <button type="button" className="cvai-action-btn" onClick={() => navigate("/settings/subscription")}>
            Переглянути тарифи
          </button>
        )}

        {message.error && (
          <div className="cvai-error" role="alert">
            <span>{message.error.message}</span>
            {isLast && (
              <button type="button" className="cvai-link-btn" onClick={onRetry} disabled={sending}>
                Спробувати ще раз
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
